/**
 * OMF (Oroblanco Manifest File) generator.
 *
 * Produces a plain-text manifest matching the format consumed by
 * download_manifest_and_package() in main/downloader.c. Mirrors the behavior
 * of omf_package.py so this library can be used either in the browser or as
 * a CLI (see ./cli.ts).
 */

export const DEFAULT_OMF_VERSION = '0.1.0'
export const FIRMWARE_LOCAL_NAME = 'FIRMWARE.BIN'
export const MANIFEST_FILE_EXT = '.omf'

const MAX_FILENAME_LENGTH = 127

export type OmfFile = {
  /** Path relative to the package root, using forward slashes. */
  path: string
  bytes: Uint8Array
}

export type GenerateOmfOptions = {
  appName: string
  files: OmfFile[]
  /** Default true. When false, omits the SHA256 column from each entry. */
  includeChecksums?: boolean
  /** Default "0.1.0". Embedded after the OMF prefix on the first line. */
  version?: string
}

export class OmfError extends Error {}

function isHidden(path: string): boolean {
  const base = path.split('/').pop() ?? ''
  return base.startsWith('.')
}

function isManifest(path: string): boolean {
  return path.toLowerCase().endsWith(MANIFEST_FILE_EXT)
}

function isBin(path: string): boolean {
  return path.toLowerCase().endsWith('.bin')
}

function bytesToHex(buf: ArrayBuffer | Uint8Array): string {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let out = ''
  for (let i = 0; i < u8.length; i++) {
    out += u8[i].toString(16).padStart(2, '0')
  }
  return out
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) {
    throw new OmfError('WebCrypto subtle API unavailable (need browser or Node 19+)')
  }
  const buf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
  const digest = await subtle.digest('SHA-256', buf as ArrayBuffer)
  return bytesToHex(digest)
}

function validatePath(path: string): void {
  if (path.length === 0) throw new OmfError('Empty file path')
  if (path.includes('\\')) throw new OmfError(`Backslash in path: ${path}`)
  if (path.includes(' ')) throw new OmfError(`Space in path (not supported by OMF): ${path}`)
  for (const seg of path.split('/')) {
    if (seg.length > MAX_FILENAME_LENGTH) {
      throw new OmfError(`Path segment exceeds ${MAX_FILENAME_LENGTH} chars: ${seg}`)
    }
  }
}

/**
 * Generate the OMF manifest text from a set of in-memory files.
 *
 * Behavior mirrors omf_package.py:
 *   - Skips dotfiles and .omf files.
 *   - Requires exactly one .bin file; lists it first as FIRMWARE.BIN.
 *   - Other files sorted alphabetically (case-insensitive) by path.
 *   - server_filename == local_filename for non-firmware files.
 *   - Trailing newline.
 */
export async function generateOmf(opts: GenerateOmfOptions): Promise<string> {
  const { appName, files } = opts
  const includeChecksums = opts.includeChecksums ?? true
  const version = opts.version ?? DEFAULT_OMF_VERSION

  if (!appName) throw new OmfError('appName is required')

  const filtered = files.filter((f) => !isHidden(f.path) && !isManifest(f.path))
  for (const f of filtered) validatePath(f.path)

  const binFiles = filtered.filter((f) => isBin(f.path))
  if (binFiles.length === 0) throw new OmfError('No .bin file found')
  if (binFiles.length > 1) {
    throw new OmfError(
      `Multiple .bin files found: ${binFiles.map((f) => f.path).join(', ')}`,
    )
  }
  const binFile = binFiles[0]
  const others = filtered
    .filter((f) => f !== binFile)
    .sort((a, b) => a.path.toLowerCase().localeCompare(b.path.toLowerCase()))

  const lines: string[] = [`OMF${version}`, appName]

  const formatLine = async (server: string, local: string, bytes: Uint8Array) => {
    if (includeChecksums) {
      const sum = await sha256Hex(bytes)
      return `${server} ${local} ${sum}`
    }
    return `${server} ${local}`
  }

  lines.push(await formatLine(binFile.path, FIRMWARE_LOCAL_NAME, binFile.bytes))
  for (const f of others) {
    lines.push(await formatLine(f.path, f.path, f.bytes))
  }

  return lines.join('\n') + '\n'
}

export type OmfEntry = {
  /** Path of the file on the server, relative to the manifest's location. */
  serverFilename: string
  /** Name the device stores the file under (FIRMWARE.BIN for the firmware). */
  localFilename: string
  /** Lowercase hex SHA-256, absent for manifests written without checksums. */
  sha256?: string
}

export type ParsedOmf = {
  version: string
  appName: string
  entries: OmfEntry[]
}

/** Parse manifest text produced by generateOmf / omf_package.py. */
export function parseOmf(text: string): ParsedOmf {
  const lines = text.split('\n')
  const header = lines[0] ?? ''
  if (!header.startsWith('OMF')) {
    throw new OmfError(`Not an OMF manifest (first line: ${JSON.stringify(header)})`)
  }
  const appName = lines[1] ?? ''
  if (!appName) throw new OmfError('OMF manifest is missing the app name line')
  const entries: OmfEntry[] = []
  for (const line of lines.slice(2)) {
    if (!line.trim()) continue
    const parts = line.split(' ')
    if (parts.length < 2 || parts.length > 3 || parts.some((p) => !p)) {
      throw new OmfError(`Malformed OMF entry line: ${JSON.stringify(line)}`)
    }
    const [serverFilename, localFilename, sha256] = parts
    entries.push(sha256 ? { serverFilename, localFilename, sha256 } : { serverFilename, localFilename })
  }
  return { version: header.slice('OMF'.length), appName, entries }
}
